import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { actions as toastrActions } from 'react-redux-toastr';
import { Formik } from 'formik';
import Paper from 'components/Paper';
import Images from 'assets/img';
import Button from 'components/Button';
import TextField from 'components/TextField';
import { cteAPI, embarcadoresAPI, fretesAPI } from 'services/apiRest';
import {
  Container,
  WrapperInput,
  SectionTitle,
  Row,
  Column,
  AutocompleteWrapper,
  AutocompleteDropdown,
  AutocompleteItem,
} from 'pages/Trip/components/ModalViagemCompleto/styles';

const formatCpfCnpj = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return value || '';
};

const getEmbarcadorLabel = (e) => {
  if (!e) return '';
  const nome = e.nome || e.nome_fantasia || e.nomeFantasia || 'Sem nome';
  const doc = e.cpfCnpj || e.cpf_cnpj || e.cnpj || e.cpf;
  const docFmt = formatCpfCnpj(doc);
  return docFmt ? `${nome} - ${docFmt}` : nome;
};

const resolverEmbarcadorPorTexto = (texto, embarcadores = []) => {
  const t = String(texto || '').trim().toLowerCase();
  if (!t) return null;

  const list = Array.isArray(embarcadores) ? embarcadores : [];

  // 1) Match exato pelo label amigável
  const exatos = list.filter((e) => getEmbarcadorLabel(e).toLowerCase() === t);
  if (exatos.length === 1) return exatos[0];

  // 2) Match exato pelo nome/nome fantasia
  const exatoNome = list.filter((e) => {
    const nome = String(e.nome || e.nome_fantasia || e.nomeFantasia || '').trim().toLowerCase();
    return nome && nome === t;
  });
  if (exatoNome.length === 1) return exatoNome[0];

  // 3) Match pelo CPF/CNPJ (somente dígitos)
  const dig = t.replace(/\D/g, '');
  if (dig.length === 11 || dig.length === 14) {
    const porDoc = list.filter((e) => String(e.cpfCnpj || e.cpf_cnpj || '').replace(/\D/g, '') === dig);
    if (porDoc.length === 1) return porDoc[0];
  }

  // 4) Se a busca atual filtra para um único embarcador, aceitar
  const candidatos = list.filter((e) => getEmbarcadorLabel(e).toLowerCase().includes(t));
  if (candidatos.length === 1) return candidatos[0];

  return null;
};

const preencherEnderecoColetaComEmbarcador = (embarcador, setValues) => {
  if (!embarcador || !setValues) return;

  const cep = String(embarcador.cep || '').replace(/\D/g, '');
  const logradouro = embarcador.endereco || '';
  const numero = embarcador.numero || '';
  const complemento = embarcador.complemento || '';
  const bairro = embarcador.bairro || '';
  const cidade = embarcador.cidade || '';
  const uf = embarcador.uf || '';

  setValues((prev) => ({
    ...prev,
    collect_zipcode: cep || prev.collect_zipcode,
    collect_point: logradouro || prev.collect_point,
    collect_number: numero || prev.collect_number,
    collect_complement: complemento || prev.collect_complement,
    collect_neighborhood: bairro || prev.collect_neighborhood,
    collect_city: cidade || prev.collect_city,
    collect_state: uf || prev.collect_state,
  }));
};

const buscarCep = async (cepValue, setValues, prefixo) => {
  const cepLimpo = String(cepValue || '').replace(/\D/g, '');
  if (cepLimpo.length !== 8) return;

  try {
    const cepResponse = await cteAPI.consultarCep(cepLimpo);
    const logradouro =
      (cepResponse.tipo_logradouro && cepResponse.logradouro)
        ? `${cepResponse.tipo_logradouro} ${cepResponse.logradouro}`.trim()
        : (cepResponse.logradouro || '');

    const updates = {};
    // garantir CEP limpo no estado do form (mesmo comportamento dos outros cadastros)
    updates[`${prefixo}_zipcode`] = cepLimpo;
    if (logradouro) updates[`${prefixo}_point`] = logradouro;
    if (cepResponse.bairro) updates[`${prefixo}_neighborhood`] = cepResponse.bairro;
    if (cepResponse.municipio) updates[`${prefixo}_city`] = cepResponse.municipio;
    if (cepResponse.uf) updates[`${prefixo}_state`] = cepResponse.uf;
    if (cepResponse.complemento) updates[`${prefixo}_complement`] = cepResponse.complemento;

    if (Object.keys(updates).length > 0 && setValues) {
      setValues((prev) => ({ ...prev, ...updates }));
    }
  } catch (error) {
    console.error('❌ Erro ao buscar CEP:', error);
    alert('CEP não encontrado. Por favor, verifique o CEP informado.');
  }
};

export default function FreteForm() {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = !!id;
  const dispatch = useDispatch();
  const isView = (location?.pathname || '').includes('/embarcadores/fretes/view/');
  const [activeTab, setActiveTab] = useState(0);
  const formikRef = useRef(null);

  const [embarcadores, setEmbarcadores] = useState([]);
  const [frete, setFrete] = useState(null);
  const [xmls, setXmls] = useState([]);
  
  // Status que permitem edição manual (antes da aprovação)
  const statusEditaveis = [null, '', 'aguardando_cotacao', 'aguardando_aprovacao'];
  const podeEditarStatus = statusEditaveis.includes(frete?.status);

  const handleRemoverXml = async (xmlId) => {
    if (!window.confirm('Deseja realmente remover este XML?')) return;
    
    try {
      await fretesAPI.removerXml(id, xmlId);
      
      dispatch(
        toastrActions.add({
          type: 'success',
          title: 'Sucesso',
          message: 'XML removido com sucesso!',
        })
      );

      // Recarregar lista
      const xmlsList = await fretesAPI.listarXmls(id);
      setXmls(Array.isArray(xmlsList) ? xmlsList : []);
    } catch (e) {
      console.error('[FreteForm] Erro ao remover XML:', e);
      dispatch(
        toastrActions.add({
          type: 'error',
          title: 'Erro',
          message: e.response?.data?.detalhes || e.message || 'Erro ao remover XML',
        })
      );
    }
  };

  // Autocomplete - Embarcador
  const [showDropdownEmbarcador, setShowDropdownEmbarcador] = useState(false);
  const [embarcadorInput, setEmbarcadorInput] = useState('');
  const dropdownEmbarcadorRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const embarcadoresList = await embarcadoresAPI.listar();
        setEmbarcadores(Array.isArray(embarcadoresList) ? embarcadoresList : []);
      } catch (e) {
        console.error('[FreteForm] Erro ao carregar listas:', e);
        setEmbarcadores([]);
      }
    })();
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const dados = await fretesAPI.buscarPorId(id);
        setFrete(dados || null);
      } catch (e) {
        console.error('[FreteForm] Erro ao buscar frete:', e);
        setFrete(null);
      }
    })();
  }, [isEdit, id]);

  // Carregar XMLs do frete
  useEffect(() => {
    if (!isEdit || !id) return;
    (async () => {
      try {
        const xmlsList = await fretesAPI.listarXmls(id);
        setXmls(Array.isArray(xmlsList) ? xmlsList : []);
      } catch (e) {
        console.error('[FreteForm] Erro ao carregar XMLs:', e);
        setXmls([]);
      }
    })();
  }, [isEdit, id]);

  // Atualizar input do embarcador quando carregar o frete (edição) ou lista
  useEffect(() => {
    // Embarcador
    if (frete?.embarcador_id && (embarcadores || []).length > 0) {
      const emb = (embarcadores || []).find((e) => e.id === frete.embarcador_id);
      if (emb) setEmbarcadorInput(getEmbarcadorLabel(emb));
    }
  }, [frete, embarcadores]);

  const initialValues = useMemo(() => ({
    numero: frete?.numero || '',
    embarcador_id: frete?.embarcador_id || '',
    motivo_cancelamento: frete?.motivo_cancelamento || '',
    data_cancelamento: frete?.data_cancelamento || '',

    // Dados da carga
    data_coleta: frete?.data_coleta || '',
    data_entrega: frete?.data_entrega || '',
    produto: frete?.produto || '',
    especie: frete?.especie || '',
    peso_total_carga: frete?.peso_total_carga || '',
    unidade_medida: frete?.unidade_medida || '',
    peso_cubado: frete?.peso_cubado || '',
    metragem_cubica_total: frete?.metragem_cubica_total || '',
    volumes: frete?.volumes || '',
    comprimento: frete?.comprimento || '',
    largura: frete?.largura || '',
    altura: frete?.altura || '',
    precisa_lona: frete?.precisa_lona ?? false,
    precisa_rastreador: frete?.precisa_rastreador ?? false,

    collect_zipcode: frete?.collect_zipcode || '',
    collect_point: frete?.collect_point || '',
    collect_number: frete?.collect_number || '',
    collect_neighborhood: frete?.collect_neighborhood || '',
    collect_complement: frete?.collect_complement || '',
    collect_city: frete?.collect_city || '',
    collect_state: frete?.collect_state || '',

    delivery_zipcode: frete?.delivery_zipcode || '',
    delivery_point: frete?.delivery_point || '',
    delivery_number: frete?.delivery_number || '',
    delivery_neighborhood: frete?.delivery_neighborhood || '',
    delivery_complement: frete?.delivery_complement || '',
    delivery_city: frete?.delivery_city || '',
    delivery_state: frete?.delivery_state || '',

    // Informações do veículo
    tipo_veiculo: frete?.tipo_veiculo || '',
    tipo_carroceria: frete?.tipo_carroceria || '',
    valor_frete: frete?.valor_frete || '',

    // Relacionamentos
    motorista_id: frete?.motorista_id || '',
    viagem_id: frete?.viagem_id || '',
  }), [frete]);

  const embarcadoresFiltrados = useMemo(() => {
    const term = String(embarcadorInput || '').toLowerCase().trim();
    const list = Array.isArray(embarcadores) ? embarcadores : [];
    if (!term) return list;
    return list.filter((e) => {
      const label = getEmbarcadorLabel(e).toLowerCase();
      return label.includes(term);
    });
  }, [embarcadores, embarcadorInput]);

  const handleSubmit = async (values) => {
    if (isView) return;
    // Segurança: garantir que o ID foi realmente setado ao selecionar no dropdown
    const embarcadorResolvido = values.embarcador_id
      ? null
      : resolverEmbarcadorPorTexto(embarcadorInput, embarcadores);
    const embarcadorIdFinal = values.embarcador_id || embarcadorResolvido?.id || null;

    if (!embarcadorIdFinal) {
      alert('Selecione um embarcador na lista (clicando no item do dropdown) antes de salvar.');
      return;
    }
    // Debug (caso necessário): ver no console do browser o que está sendo enviado
    // eslint-disable-next-line no-console
    console.log('[FreteForm] Salvando frete com embarcador_id:', embarcadorIdFinal);

    const payload = {
      embarcador_id: embarcadorIdFinal,

      // Dados da carga
      data_coleta: values.data_coleta || null,
      data_entrega: values.data_entrega || null,
      produto: values.produto || '',
      especie: values.especie || '',
      peso_total_carga: values.peso_total_carga !== '' ? Number(values.peso_total_carga) : null,
      unidade_medida: values.unidade_medida || '',
      peso_cubado: values.peso_cubado !== '' ? Number(values.peso_cubado) : null,
      metragem_cubica_total: values.metragem_cubica_total !== '' ? Number(values.metragem_cubica_total) : null,
      volumes: values.volumes !== '' ? Number(values.volumes) : null,
      comprimento: values.comprimento !== '' ? Number(values.comprimento) : null,
      largura: values.largura !== '' ? Number(values.largura) : null,
      altura: values.altura !== '' ? Number(values.altura) : null,
      precisa_lona: !!values.precisa_lona,
      precisa_rastreador: !!values.precisa_rastreador,

      collect_zipcode: (values.collect_zipcode || '').replace(/\D/g, '') || null,
      collect_point: values.collect_point || '',
      collect_number: values.collect_number || '',
      collect_neighborhood: values.collect_neighborhood || '',
      collect_complement: values.collect_complement || '',
      collect_city: values.collect_city || '',
      collect_state: values.collect_state || '',

      delivery_zipcode: (values.delivery_zipcode || '').replace(/\D/g, '') || null,
      delivery_point: values.delivery_point || '',
      delivery_number: values.delivery_number || '',
      delivery_neighborhood: values.delivery_neighborhood || '',
      delivery_complement: values.delivery_complement || '',
      delivery_city: values.delivery_city || '',
      delivery_state: values.delivery_state || '',

      // Informações do veículo
      tipo_veiculo: values.tipo_veiculo || null,
      tipo_carroceria: values.tipo_carroceria || null,
      valor_frete: values.valor_frete !== '' ? Number(values.valor_frete) : null,

      // Relacionamentos
      motorista_id: values.motorista_id || null,
    };

    if (isEdit) {
      await fretesAPI.atualizar(id, payload);
    } else {
      await fretesAPI.criar(payload);
    }

    history.push('/embarcadores/fretes');
  };

  return (
    <div style={{ padding: '20px', width: '100%' }}>
      <Paper
        title={isView ? 'Visualizar Frete' : (isEdit ? 'Editar Frete' : 'Novo Frete')}
        icon={Images.IconPaper}
        rightContent={
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Button label="Voltar" type="button" onClick={() => history.push('/embarcadores/fretes')} />
            {!isView ? (
              <Button
                label={isEdit ? 'Salvar' : 'Criar'}
                type="button"
                onClick={() => formikRef.current?.handleSubmit()}
              />
            ) : null}
          </div>
        }
      >
        {/* Tabs */}
        <div style={{ display: 'flex', marginBottom: '20px', borderBottom: '1px solid #ddd' }}>
          {['Dados da Carga', 'Endereço de Coleta', 'Endereço de Entrega', isEdit ? 'XMLs Anexados' : null].filter(Boolean).map((tab, index) => (
            <button
              key={tab}
              onClick={() => setActiveTab(index)}
              type="button"
              style={{
                padding: '10px 20px',
                border: 'none',
                borderRadius: '5px',
                background: activeTab === index ? '#122D58' : 'transparent',
                color: activeTab === index ? 'white' : '#122D58',
                cursor: 'pointer',
                borderBottom: activeTab === index ? '2px solid #122D58' : 'none',
              }}
            >
              {tab}
            </button>
          ))}
        </div>
        <Container>
          <Formik innerRef={formikRef} initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
            {({ values, handleChange, setValues, setFieldValue }) => (
              <form onSubmit={(e) => { e.preventDefault(); if (!isView) formikRef.current?.handleSubmit(); }}>
                <SectionTitle>Identificação</SectionTitle>
                <Row>
                  <Column>
                    <WrapperInput>
                      <TextField
                        label="Referência do Frete"
                        name="numero"
                        value={values.numero ? `FRETE Nº ${values.numero}` : 'Gerado automaticamente'}
                        fullWidth
                        color="secondary"
                        disabled
                        helperText="A referência é incremental e gerada automaticamente."
                        InputLabelProps={{ shrink: true }}
                      />
                    </WrapperInput>
                  </Column>
                  <Column>
                    <WrapperInput>
                      <AutocompleteWrapper ref={dropdownEmbarcadorRef}>
                        <TextField
                          label="Embarcador"
                          name="embarcador_autocomplete"
                          placeholder="Digite para buscar o embarcador"
                          value={embarcadorInput}
                          onChange={(e) => {
                            const value = e.target.value;
                            setEmbarcadorInput(value);
                            setShowDropdownEmbarcador(true);
                            if (!value) {
                              setFieldValue('embarcador_id', '');
                            }
                          }}
                          disabled={isView}
                          onFocus={() => {
                            if ((embarcadores || []).length > 0) setShowDropdownEmbarcador(true);
                          }}
                          onBlur={() => {
                            setTimeout(() => setShowDropdownEmbarcador(false), 200);
                          }}
                          FormHelperTextProps={{ component: 'div' }}
                          helperText={
                            <div>
                              {(embarcadores || []).length > 0
                                ? `${(embarcadores || []).length} embarcador(es) disponível(is)`
                                : 'Nenhum embarcador cadastrado'}
                              {values.embarcador_id ? (
                                <div style={{ marginTop: '4px', fontSize: '12px', color: '#666' }}>
                                  ID selecionado: {(values.embarcador_id || '').slice(0, 8)}...
                                </div>
                              ) : null}
                            </div>
                          }
                          fullWidth
                          color="secondary"
                          InputLabelProps={{ shrink: true }}
                        />
                        {showDropdownEmbarcador && embarcadoresFiltrados.length > 0 && (
                          <AutocompleteDropdown>
                            {embarcadoresFiltrados.slice(0, 20).map((e) => (
                              <AutocompleteItem
                                key={e.id}
                                onClick={() => {
                                  if (isView) return;
                                  setFieldValue('embarcador_id', e.id);
                                  setEmbarcadorInput(getEmbarcadorLabel(e));
                                  // Ao selecionar embarcador, preencher Endereço de Coleta com o endereço do embarcador
                                  preencherEnderecoColetaComEmbarcador(e, setValues);
                                  setShowDropdownEmbarcador(false);
                                }}
                              >
                                <div>
                                  <strong>{e.nome || e.nomeFantasia || e.nome_fantasia || 'Sem nome'}</strong>
                                  {(e.cpfCnpj || e.cpf_cnpj) ? (
                                    <span style={{ marginLeft: '8px', fontSize: '12px', color: '#666' }}>
                                      {formatCpfCnpj(e.cpfCnpj || e.cpf_cnpj)}
                                    </span>
                                  ) : null}
                                </div>
                              </AutocompleteItem>
                            ))}
                            {embarcadoresFiltrados.length > 20 && (
                              <AutocompleteItem style={{ textAlign: 'center', color: '#999', fontStyle: 'italic' }}>
                                ... e mais {embarcadoresFiltrados.length - 20} embarcador(es)
                              </AutocompleteItem>
                            )}
                          </AutocompleteDropdown>
                        )}
                      </AutocompleteWrapper>
                    </WrapperInput>
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <WrapperInput>
                      <TextField
                        select
                        label="Tipo do Veículo"
                        name="tipo_veiculo"
                        value={values.tipo_veiculo || ''}
                        onChange={handleChange}
                        fullWidth
                        color="secondary"
                        disabled={isView}
                        InputLabelProps={{ shrink: true }}
                        SelectProps={{
                          native: true,
                        }}
                      >
                        <option value="">Selecione...</option>
                        <option value="Truck">Truck</option>
                        <option value="Bitruck">Bitruck</option>
                        <option value="Toco">Toco</option>
                        <option value="Rodotrem">Rodotrem</option>
                        <option value="Bitrem">Bitrem</option>
                        <option value="Vanderleia">Vanderleia</option>
                        <option value="Carreta LS">Carreta LS</option>
                        <option value="Carreta">Carreta</option>
                      </TextField>
                    </WrapperInput>
                  </Column>
                  <Column>
                    <WrapperInput>
                      <TextField
                        select
                        label="Tipo da Carroceria"
                        name="tipo_carroceria"
                        value={values.tipo_carroceria || ''}
                        onChange={handleChange}
                        fullWidth
                        color="secondary"
                        disabled={isView}
                        InputLabelProps={{ shrink: true }}
                        SelectProps={{
                          native: true,
                        }}
                      >
                        <option value="">Selecione...</option>
                        <option value="Baú">Baú</option>
                        <option value="Frigorífica">Frigorífica</option>
                        <option value="Tanque">Tanque</option>
                        <option value="Sider">Sider</option>
                        <option value="Caçamba">Caçamba</option>
                        <option value="Grade baixa">Grade baixa</option>
                        <option value="Graneleira">Graneleira</option>
                        <option value="Prancha">Prancha</option>
                        <option value="Plataforma">Plataforma</option>
                      </TextField>
                    </WrapperInput>
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <WrapperInput>
                      <TextField
                        label="Valor do Frete (R$)"
                        name="valor_frete"
                        type="number"
                        value={values.valor_frete}
                        onChange={handleChange}
                        fullWidth
                        color="secondary"
                        disabled={isView}
                        placeholder="0.00"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          step: "0.01",
                          min: "0"
                        }}
                      />
                    </WrapperInput>
                  </Column>
                  <Column />
                </Row>
                {values.status === 'cancelado' && values.motivo_cancelamento && (
                  <>
                    <Row>
                      <Column>
                        <WrapperInput>
                          <TextField
                            label="Motivo do Cancelamento"
                            name="motivo_cancelamento"
                            value={values.motivo_cancelamento}
                            fullWidth
                            color="secondary"
                            disabled
                            multiline
                            rows={3}
                            InputLabelProps={{ shrink: true }}
                          />
                        </WrapperInput>
                      </Column>
                      <Column>
                        <WrapperInput>
                          <TextField
                            label="Data do Cancelamento"
                            name="data_cancelamento"
                            value={
                              values.data_cancelamento
                                ? new Date(values.data_cancelamento).toLocaleString('pt-BR', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                : '-'
                            }
                            fullWidth
                            color="secondary"
                            disabled
                            InputLabelProps={{ shrink: true }}
                          />
                        </WrapperInput>
                      </Column>
                    </Row>
                  </>
                )}

                {activeTab === 0 && (
                  <>
                    <SectionTitle>Dados da Carga</SectionTitle>
                    <Row>
                      <Column>
                        <WrapperInput>
                          <TextField
                            label="Data da Coleta"
                            name="data_coleta"
                            type="date"
                            value={values.data_coleta}
                            onChange={handleChange}
                            fullWidth
                            color="secondary"
                            disabled={isView}
                            InputLabelProps={{ shrink: true }}
                          />
                        </WrapperInput>
                      </Column>
                      <Column>
                        <WrapperInput>
                          <TextField
                            label="Data da Entrega"
                            name="data_entrega"
                            type="date"
                            value={values.data_entrega}
                            onChange={handleChange}
                            fullWidth
                            color="secondary"
                            disabled={isView}
                            InputLabelProps={{ shrink: true }}
                          />
                        </WrapperInput>
                      </Column>
                    </Row>
                    <Row>
                      <Column>
                        <WrapperInput>
                          <TextField label="Produto" name="produto" value={values.produto} onChange={handleChange} fullWidth color="secondary" disabled={isView} />
                        </WrapperInput>
                      </Column>
                      <Column>
                        <WrapperInput>
                          <TextField label="Espécie" name="especie" value={values.especie} onChange={handleChange} fullWidth color="secondary" disabled={isView} />
                        </WrapperInput>
                      </Column>
                    </Row>
                    <Row>
                      <Column>
                        <WrapperInput>
                          <TextField label="Peso total da Carga" name="peso_total_carga" type="number" value={values.peso_total_carga} onChange={handleChange} fullWidth color="secondary" disabled={isView} />
                        </WrapperInput>
                      </Column>
                      <Column>
                        <WrapperInput>
                          <TextField label="Unidade de medida" name="unidade_medida" value={values.unidade_medida} onChange={handleChange} fullWidth color="secondary" placeholder="Ex: kg" disabled={isView} />
                        </WrapperInput>
                      </Column>
                    </Row>
                    <Row>
                      <Column>
                        <WrapperInput>
                          <TextField label="Peso Cubado" name="peso_cubado" type="number" value={values.peso_cubado} onChange={handleChange} fullWidth color="secondary" disabled={isView} />
                        </WrapperInput>
                      </Column>
                      <Column>
                        <WrapperInput>
                          <TextField label="Metragem Cúbica total" name="metragem_cubica_total" type="number" value={values.metragem_cubica_total} onChange={handleChange} fullWidth color="secondary" disabled={isView} />
                        </WrapperInput>
                      </Column>
                    </Row>
                    <Row>
                      <Column>
                        <WrapperInput>
                          <TextField label="Volumes" name="volumes" type="number" value={values.volumes} onChange={handleChange} fullWidth color="secondary" disabled={isView} />
                        </WrapperInput>
                      </Column>
                      <Column />
                    </Row>
                    <Row>
                      <Column>
                        <WrapperInput>
                          <TextField label="Comprimento" name="comprimento" type="number" value={values.comprimento} onChange={handleChange} fullWidth color="secondary" disabled={isView} />
                        </WrapperInput>
                      </Column>
                      <Column>
                        <WrapperInput>
                          <TextField label="Largura" name="largura" type="number" value={values.largura} onChange={handleChange} fullWidth color="secondary" disabled={isView} />
                        </WrapperInput>
                      </Column>
                    </Row>
                    <Row>
                      <Column>
                        <WrapperInput>
                          <TextField label="Altura" name="altura" type="number" value={values.altura} onChange={handleChange} fullWidth color="secondary" disabled={isView} />
                        </WrapperInput>
                      </Column>
                      <Column />
                    </Row>
                    <Row>
                      <Column>
                        <WrapperInput>
                          <TextField
                            label="Precisa de Lona"
                            name="precisa_lona"
                            value={values.precisa_lona ? 'sim' : 'nao'}
                            onChange={(e) => setFieldValue('precisa_lona', e.target.value === 'sim')}
                            select
                            fullWidth
                            color="secondary"
                            disabled={isView}
                          >
                            <option value="nao">Não</option>
                            <option value="sim">Sim</option>
                          </TextField>
                        </WrapperInput>
                      </Column>
                      <Column>
                        <WrapperInput>
                          <TextField
                            label="Precisa de rastreador"
                            name="precisa_rastreador"
                            value={values.precisa_rastreador ? 'sim' : 'nao'}
                            onChange={(e) => setFieldValue('precisa_rastreador', e.target.value === 'sim')}
                            select
                            fullWidth
                            color="secondary"
                            disabled={isView}
                          >
                            <option value="nao">Não</option>
                            <option value="sim">Sim</option>
                          </TextField>
                        </WrapperInput>
                      </Column>
                    </Row>
                  </>
                )}

                {activeTab === 1 && (
                  <>
                    <SectionTitle>Endereço de Coleta</SectionTitle>
                    <Row>
                      <Column>
                        <WrapperInput>
                          <TextField
                            label="CEP"
                            name="collect_zipcode"
                            value={values.collect_zipcode}
                            onChange={handleChange}
                            onBlur={() => buscarCep(values.collect_zipcode, setValues, 'collect')}
                            fullWidth
                            color="secondary"
                            disabled={isView}
                          />
                        </WrapperInput>
                      </Column>
                      <Column>
                        <WrapperInput>
                          <TextField label="Endereço" name="collect_point" value={values.collect_point} onChange={handleChange} fullWidth color="secondary" disabled={isView} />
                        </WrapperInput>
                      </Column>
                    </Row>
                    <Row>
                      <Column>
                        <WrapperInput>
                          <TextField label="Número" name="collect_number" value={values.collect_number} onChange={handleChange} fullWidth color="secondary" disabled={isView} />
                        </WrapperInput>
                      </Column>
                      <Column>
                        <WrapperInput>
                          <TextField label="Complemento" name="collect_complement" value={values.collect_complement} onChange={handleChange} fullWidth color="secondary" disabled={isView} />
                        </WrapperInput>
                      </Column>
                    </Row>
                    <Row>
                      <Column>
                        <WrapperInput>
                          <TextField label="Bairro" name="collect_neighborhood" value={values.collect_neighborhood} onChange={handleChange} fullWidth color="secondary" disabled={isView} />
                        </WrapperInput>
                      </Column>
                      <Column>
                        <WrapperInput>
                          <TextField label="Cidade" name="collect_city" value={values.collect_city} onChange={handleChange} fullWidth color="secondary" disabled={isView} />
                        </WrapperInput>
                      </Column>
                    </Row>
                    <Row>
                      <Column>
                        <WrapperInput>
                          <TextField
                            label="UF"
                            name="collect_state"
                            value={values.collect_state}
                            onChange={handleChange}
                            fullWidth
                            color="secondary"
                            disabled={isView}
                            inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                          />
                        </WrapperInput>
                      </Column>
                      <Column />
                    </Row>
                  </>
                )}

                {activeTab === 2 && (
                  <>
                    <SectionTitle>Endereço de Entrega</SectionTitle>
                    <Row>
                      <Column>
                        <WrapperInput>
                          <TextField
                            label="CEP"
                            name="delivery_zipcode"
                            value={values.delivery_zipcode}
                            onChange={handleChange}
                            onBlur={() => buscarCep(values.delivery_zipcode, setValues, 'delivery')}
                            fullWidth
                            color="secondary"
                            disabled={isView}
                          />
                        </WrapperInput>
                      </Column>
                      <Column>
                        <WrapperInput>
                          <TextField label="Endereço" name="delivery_point" value={values.delivery_point} onChange={handleChange} fullWidth color="secondary" disabled={isView} />
                        </WrapperInput>
                      </Column>
                    </Row>
                    <Row>
                      <Column>
                        <WrapperInput>
                          <TextField label="Número" name="delivery_number" value={values.delivery_number} onChange={handleChange} fullWidth color="secondary" disabled={isView} />
                        </WrapperInput>
                      </Column>
                      <Column>
                        <WrapperInput>
                          <TextField label="Complemento" name="delivery_complement" value={values.delivery_complement} onChange={handleChange} fullWidth color="secondary" disabled={isView} />
                        </WrapperInput>
                      </Column>
                    </Row>
                    <Row>
                      <Column>
                        <WrapperInput>
                          <TextField label="Bairro" name="delivery_neighborhood" value={values.delivery_neighborhood} onChange={handleChange} fullWidth color="secondary" disabled={isView} />
                        </WrapperInput>
                      </Column>
                      <Column>
                        <WrapperInput>
                          <TextField label="Cidade" name="delivery_city" value={values.delivery_city} onChange={handleChange} fullWidth color="secondary" disabled={isView} />
                        </WrapperInput>
                      </Column>
                    </Row>
                    <Row>
                      <Column>
                        <WrapperInput>
                          <TextField
                            label="UF"
                            name="delivery_state"
                            value={values.delivery_state}
                            onChange={handleChange}
                            fullWidth
                            color="secondary"
                            disabled={isView}
                            inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                          />
                        </WrapperInput>
                      </Column>
                      <Column />
                    </Row>
                  </>
                )}

                {/* Tab 3: XMLs Anexados */}
                {activeTab === 3 && isEdit && (
                  <>
                    <SectionTitle>XMLs Anexados</SectionTitle>
                    {xmls.length === 0 ? (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                        Nenhum XML anexado
                      </div>
                    ) : (
                      <div style={{ marginTop: '20px' }}>
                        {xmls.map((xml) => (
                          <div
                            key={xml.id}
                            style={{
                              padding: '15px',
                              marginBottom: '10px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: 'bold' }}>📄 {xml.xml_filename}</div>
                              <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                                {xml.xml_tipo && `Tipo: ${xml.xml_tipo}`}
                                {xml.xml_numero && ` | Nº: ${xml.xml_numero}`}
                              </div>
                              {xml.xml_chave && (
                                <div style={{ fontSize: '11px', color: '#999', marginTop: '3px' }}>
                                  Chave: {xml.xml_chave}
                                </div>
                              )}
                            </div>
                            {!isView && (
                              <Button
                                label="Remover"
                                type="button"
                                onClick={() => handleRemoverXml(xml.id)}
                                style={{ backgroundColor: '#f44336' }}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </form>
            )}
          </Formik>
        </Container>
      </Paper>

      {/* Modal de Anexar XMLs */}
    </div>
  );
}


